import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { createColumnHelper, type ColumnDef } from '@tanstack/react-table'
import { FileText, Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
  cancelServiceInvoice,
  createServiceInvoice,
  serviceInvoiceFormSchema,
  serviceInvoiceKeys,
  serviceInvoicesQueryOptions,
  toServiceInvoiceCreateBody,
  type ServiceInvoiceFormValues,
} from '@/features/billing/service-invoices'
import { MoneyText } from '@/features/schedules/components/money-text'
import { stakeholdersQueryOptions } from '@/features/stakeholders/api'
import { ApiError } from '@/shared/api/api-error'
import type { ServiceInvoiceOut } from '@/shared/api/types'
import { useIdempotencyKey } from '@/shared/api/use-idempotency-key'
import { useApiErrorMessage } from '@/shared/i18n/use-api-error-message'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Drawer } from '@/shared/ui/drawer'
import { EmptyState } from '@/shared/ui/empty-state'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { MoneyInput } from '@/shared/ui/money-input'
import { SelectNative } from '@/shared/ui/select-native'
import { PageHeader } from '@/widgets/app-shell/page-header'
import { DataTable } from '@/widgets/data-table/data-table'

export const Route = createFileRoute('/_authed/recebimentos/nfse')({
  loader: ({ context }) => context.queryClient.ensureQueryData(serviceInvoicesQueryOptions()),
  component: NfsePage,
})

const col = createColumnHelper<ServiceInvoiceOut>()

const STATUS_TONE = { pending: 'info', issued: 'positive', canceled: 'neutral' } as const

function NfsePage() {
  const { t } = useTranslation(['billing', 'nav', 'common'])
  const qc = useQueryClient()
  const mensagemDeErro = useApiErrorMessage()
  const [novaAberta, setNovaAberta] = useState(false)

  const { data, isLoading, isFetching, error, refetch } = useQuery(serviceInvoicesQueryOptions())
  // Cancelado sumiu da interface: nota cancelada não aparece na lista (o back
  // ainda pode devolvê-la, então filtramos antes de renderizar).
  const dataVisivel =
    data === undefined
      ? undefined
      : { ...data, items: data.items.filter((inv) => inv.status !== 'canceled') }

  // Destruturado: o objeto do useMutation não é referencialmente estável e não
  // pode entrar nas deps do useMemo das colunas.
  const { mutate: cancelarNota, isPending: cancelando } = useMutation({
    mutationFn: (id: string) => cancelServiceInvoice(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: serviceInvoiceKeys.lists() })
      toast.success(t('billing:invoice.canceled'))
    },
    onError: (err: unknown) => {
      toast.error(mensagemDeErro(err))
    },
  })

  const columns = useMemo(
    () =>
      [
        col.accessor((r) => r.stakeholder.name, {
          id: 'stakeholder',
          header: t('billing:columns.contact'),
        }),
        col.accessor('description', {
          id: 'description',
          header: t('billing:columns.description'),
        }),
        col.accessor('amount', {
          id: 'amount',
          header: t('billing:columns.amount'),
          cell: (c) => <MoneyText value={c.getValue()} tone="income" />,
        }),
        col.accessor('status', {
          id: 'status',
          header: t('billing:columns.status'),
          cell: (c) => (
            <Badge tone={STATUS_TONE[c.getValue()]}>
              {t(`billing:invoice.status.${c.getValue()}` as 'billing:invoice.status.pending')}
            </Badge>
          ),
        }),
        col.display({
          id: 'actions',
          header: () => null,
          cell: (c) => {
            const inv = c.row.original
            if (inv.status === 'canceled') return null
            return (
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  size="xs"
                  disabled={cancelando}
                  onClick={() => {
                    cancelarNota(inv.id)
                  }}
                >
                  {t('common:actions.cancel')}
                </Button>
              </div>
            )
          },
        }),
      ] as ColumnDef<ServiceInvoiceOut>[],
    [t, cancelarNota, cancelando],
  )

  const botaoNova = (
    <Button
      variant="primary"
      onClick={() => {
        setNovaAberta(true)
      }}
    >
      <Plus aria-hidden />
      {t('billing:invoice.new')}
    </Button>
  )

  return (
    <>
      <PageHeader title={t('nav:receivables.nfse')} actions={botaoNova} />
      <div className="p-6">
        <DataTable
          columns={columns}
          page={dataVisivel}
          isLoading={isLoading}
          isFetching={isFetching}
          error={error}
          onRetry={() => void refetch()}
          getRowId={(r) => r.id}
          sorting={[{ id: 'stakeholder', desc: false }]}
          onSortingChange={() => undefined}
          ariaLabel={t('billing:invoice.ariaTable')}
          empty={
            <EmptyState
              icon={FileText}
              title={t('billing:invoice.empty.title')}
              description={t('billing:invoice.empty.description')}
              action={botaoNova}
            />
          }
        />
      </div>

      <NovaNotaDrawer
        key={novaAberta ? 'aberta' : 'fechada'}
        open={novaAberta}
        onOpenChange={setNovaAberta}
      />
    </>
  )
}

function NovaNotaDrawer({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation(['billing', 'common', 'validation'])
  const qc = useQueryClient()
  const mensagemDeErro = useApiErrorMessage()
  const { key: idempotencyKey, renew } = useIdempotencyKey()

  const contatos = useQuery(stakeholdersQueryOptions({ order_by: 'name' }))

  const form = useForm<ServiceInvoiceFormValues>({
    resolver: zodResolver(serviceInvoiceFormSchema),
    defaultValues: { stakeholderId: '', description: '', amount: '', scheduleId: '' },
  })

  const criar = useMutation({
    mutationFn: (v: ServiceInvoiceFormValues) =>
      createServiceInvoice(toServiceInvoiceCreateBody(v), idempotencyKey),
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: serviceInvoiceKeys.lists() })
      toast.success(
        res.created ? t('billing:invoice.created') : t('billing:invoice.alreadyCreated'),
      )
      renew()
      onOpenChange(false)
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.isValidation) {
        for (const [campo, msg] of Object.entries(err.fieldErrors())) {
          form.setError(campo as keyof ServiceInvoiceFormValues, { message: msg })
        }
        return
      }
      toast.error(mensagemDeErro(err))
    },
  })

  const onSubmit = form.handleSubmit((v) => {
    criar.mutate(v)
  })

  const NS = /^(validation|common):/
  const traduz = (m: string | undefined): string | undefined =>
    m === undefined ? undefined : NS.test(m) ? t(m as 'validation:required') : m

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={t('billing:invoice.new')}
      footer={
        <>
          <Button variant="primary" onClick={() => void onSubmit()} disabled={criar.isPending}>
            {criar.isPending ? t('common:states.loading') : t('common:actions.save')}
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              onOpenChange(false)
            }}
          >
            {t('common:actions.cancel')}
          </Button>
        </>
      }
    >
      {/* `partial` no nav: registra a nota; a EMISSÃO na prefeitura é adapter
          futuro — o status nasce "pendente" e só vira "emitida" quando existir. */}
      <p
        role="status"
        className="border-caution-bg bg-caution-bg text-caution mb-4 rounded-md border px-3 py-2 text-sm"
      >
        {t('billing:invoice.partialNote')}
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          void onSubmit()
        }}
        className="flex flex-col gap-4"
        noValidate
      >
        <div className="flex flex-col gap-1">
          <Label htmlFor="stakeholderId">
            {t('billing:columns.contact')}
            <span className="text-danger ml-0.5">*</span>
          </Label>
          <SelectNative
            id="stakeholderId"
            {...form.register('stakeholderId')}
            aria-invalid={form.formState.errors.stakeholderId !== undefined}
          >
            <option value="">{t('billing:invoice.contactPlaceholder')}</option>
            {(contatos.data?.items ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </SelectNative>
          {traduz(form.formState.errors.stakeholderId?.message) !== undefined && (
            <span role="alert" className="text-danger text-2xs">
              {traduz(form.formState.errors.stakeholderId?.message)}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <Label htmlFor="description">
            {t('billing:columns.description')}
            <span className="text-danger ml-0.5">*</span>
          </Label>
          <Input
            id="description"
            {...form.register('description')}
            aria-invalid={form.formState.errors.description !== undefined}
          />
          {traduz(form.formState.errors.description?.message) !== undefined && (
            <span role="alert" className="text-danger text-2xs">
              {traduz(form.formState.errors.description?.message)}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <Label htmlFor="amount">
            {t('billing:columns.amount')}
            <span className="text-danger ml-0.5">*</span>
          </Label>
          <MoneyInput
            id="amount"
            // eslint-disable-next-line react-hooks/incompatible-library -- form.watch() do RHF não é memoizável (React Compiler pula, esperado)
            value={form.watch('amount')}
            onValueChange={(v) => {
              form.setValue('amount', v, { shouldValidate: true })
            }}
            aria-invalid={form.formState.errors.amount !== undefined}
          />
          {traduz(form.formState.errors.amount?.message) !== undefined && (
            <span role="alert" className="text-danger text-2xs">
              {traduz(form.formState.errors.amount?.message)}
            </span>
          )}
        </div>
      </form>
    </Drawer>
  )
}
